/**
 * Immersive routes: the ones that are themselves a full workspace (#1831).
 *
 * On these routes `MainLayout` steps back — the sidebar is never a pinned
 * rail, the content offset goes away, neither page footer renders, and the
 * Header drops its menu button.
 *
 * #2266 (Victor 2026-10-06) took `/chat` out: the companion conversation lives
 * on the parent home, and `/chat/:conversationId` renders the same thread
 * inside the normal app shell, so a push deep link lands with the sidebar,
 * the BottomNav and the back arrow in place. No route is immersive today; the
 * mechanism stays because the shell's branches are keyed off it.
 *
 * The rule is derived from the URL and NOTHING else. It must never write to
 * `trf_sidebar_mode` or `trf_sidebar_expanded`: leaving an immersive route has
 * to restore exactly the setting the user chose.
 *
 * Lives beside `parent-nav.ts` / `trfer-nav.ts` rather than inside
 * `MainLayout.tsx` so the layout module keeps exporting only its component
 * (`react-refresh/only-export-components`).
 */

/** Exact-match patterns for the immersive routes. Empty since #2266. */
const IMMERSIVE_ROUTES: ReadonlyArray<RegExp> = [];

/** True for an immersive route, false everywhere else (everywhere, since #2266). */
export function isImmersiveRoute(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return IMMERSIVE_ROUTES.some((pattern) => pattern.test(path));
}

/**
 * True for `/chat/:conversationId` — one thread at its own address. The
 * Header resolves this route's back arrow against `/chat`, whose canonical
 * parent is the home where the conversation lives (`parentFor`), so a push
 * deep link leaves for Today in one hop instead of landing on an empty chat.
 */
export function isChatThreadRoute(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return /^\/chat\/[^/]+$/.test(path);
}
